import dayjs from 'dayjs';
import { jwtDecode as jwt_decode } from 'jwt-decode';
import moment from 'moment-timezone';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { getActiveToken } from '../helper/tokenStorage';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

// TypeScript interfaces
interface JWTTokenPayload {
  user_time_zone?: string;
  user_id?: number;
  [key: string]: unknown;
}

interface Team {
  id: string;
  permissions?: {
    create_self?: boolean;
  };
}

interface SendToItem {
  id: string;
  select_all: boolean;
  users: string[];
  create_self: boolean;
}

interface CreateSendToResult {
  additionalRecipientValues: string[];
  sendToArray: SendToItem[];
}

interface ShipmentLocation {
  address?:
    | string
    | {
        city?: string | null;
        line1?: string | null;
        line2?: string | null;
        postalCode?: string | null;
        countryCode?: string | null;
        stateProvince?: string | null;
      };
  parsed_address?: {
    PlaceName?: string;
    StateName?: string;
    ZipCode?: string;
    SubaddressIdentifier?: string;
    Recipient?: string;
  };
}

/**
 * Plays a beep sound from the public audio file
 *
 * @returns Promise that resolves when audio playback starts or fails
 */
export const playBeepSound = (): Promise<void> => {
  const audio = new Audio('/beep.mp3');
  return audio
    .play()
    .catch((error: Error) =>
      console.error('🔇 Error playing beep sound:', error)
    );
};

export interface UserTimeZone {
  /** Timezone identifier, e.g., "America/New_York" */
  name: string;
  /** UTC offset in minutes (negative = west of UTC), e.g., -300 for EST */
  offsetMinutes: number;
}

/**
 * Gets the user's timezone from JWT token or falls back to browser timezone
 *
 * @returns Object with timezone name and UTC offset in minutes
 */
export function getUserTimeZone(): UserTimeZone {
  const zones: string[] = moment.tz.names();
  let timeZone: string | undefined;

  // Try to get timezone from active JWT token first (preview or real)
  const token = getActiveToken();
  if (token) {
    try {
      const decoded = jwt_decode<JWTTokenPayload>(token);
      if (decoded?.user_time_zone) {
        timeZone = decoded.user_time_zone;
      }
    } catch (err: unknown) {
      console.error('Invalid token:', err);
    }
  }

  // Use saved preferred timezone when user has set it in settings but JWT not yet refreshed
  if (!timeZone && typeof localStorage !== 'undefined') {
    const saved = localStorage.getItem('user_time_zone');
    if (saved?.trim()) timeZone = saved.trim();
  }

  // Fall back to browser timezone if token-based detection failed
  if (!timeZone) {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  const matchedZone = zones.find((x: string) => x === timeZone);
  const name = matchedZone ?? timeZone;
  const offsetMinutes = moment.tz(name).utcOffset();

  return { name, offsetMinutes };
}

/**
 * Gets a formatted string describing the user's timezone setting
 *
 * @returns Formatted string like "Timezone set to America/New_York"
 */
export function getFormattedUserTimeZone(): string {
  const { name } = getUserTimeZone();
  return `Timezone set to ${name}`;
}

/**
 * Gets the user's preferred date format from the JWT token, for call sites
 * (plain utils, static chart configs) that can't use the useUserDateTimeFormat
 * hook. Falls back to 'MM/DD/YYYY' when unset or the token is missing.
 *
 * @returns One of 'MM/DD/YYYY' | 'DD/MM/YYYY' | 'YYYY/MM/DD'
 */
export function getUserDateFormat(): string {
  const token = getActiveToken();
  if (token) {
    try {
      const decoded = jwt_decode<JWTTokenPayload & { date_format?: string }>(
        token
      );
      if (decoded?.date_format) {
        return decoded.date_format;
      }
    } catch (err: unknown) {
      console.error('Invalid token:', err);
    }
  }
  return 'MM/DD/YYYY';
}

const CURRENCY_SIGNS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
};

/**
 * Maps a currency code (e.g. 'USD', 'EUR', 'GBP' — see `currencyOptions` in
 * common/static_data.ts) to its display sign. Falls back to '$' for an
 * unset or unrecognized code.
 */
export function getCurrencySign(currency?: string | null): string {
  if (!currency) return '$';
  return CURRENCY_SIGNS[currency.toUpperCase()] || '$';
}

/**
 * Checks if a company name represents Zoomlogi Support team
 *
 * @param companyName - The company name to check
 * @returns True if the company name matches "zoomlogi support" (case-insensitive), false otherwise
 */
export const isZoomlogiSupport = (companyName: unknown): boolean => {
  return companyName?.toString()?.trim()?.toLowerCase() === 'zoomlogi support';
};

// Insight Chat access, shared by the header icon and Help's hand-off row.
// Either allowlist (old ai_insights_chatbot or new agentcore_chatbot) is
// enough; never during a preview session or for Zoomlogi support.
export const canUseAiInsightsChat = (
  token:
    | {
        company_name?: string;
        ai_insights_chatbot?: boolean;
        agentcore_chatbot?: boolean;
      }
    | null
    | undefined,
  isPreview: boolean
): boolean =>
  !isPreview &&
  !isZoomlogiSupport(token?.company_name) &&
  (token?.ai_insights_chatbot === true || token?.agentcore_chatbot === true);

/**
 * Transforms user selection data into structured team and recipient arrays
 *
 * Processes user selections to create structured data for team-based notifications.
 * Handles both individual user selections within teams and "ALL_MEMBERS" selections.
 * Also processes additional recipients that don't follow the team structure.
 *
 * @param users - Array of user selection strings in format "teamId::userId" or "teamId::ALL_MEMBERS"
 * @param teams - Array of team objects containing id and permissions
 * @returns Object containing structured sendToArray and additionalRecipientValues
 */
export function CreateSendToArray(
  users: string[] | undefined,
  teams: Team[] | undefined
): CreateSendToResult {
  const selectedValues: string[] = users || [];
  const sendToMap: Record<
    string,
    {
      id: string;
      users: string[];
      select_all: boolean;
    }
  > = {};
  const additionalRecipientValues: string[] = [];
  selectedValues.forEach((val: string) => {
    if (!val.includes('::')) {
      additionalRecipientValues.push(val);
      return;
    }

    if (val.includes('::ALL_MEMBERS')) {
      const [teamId] = val.split('::');
      sendToMap[teamId] = {
        id: teamId,
        users: [],
        select_all: true,
      };
    } else {
      const [teamId, userId] = val.split('::');
      if (!sendToMap[teamId]) {
        sendToMap[teamId] = {
          id: teamId,
          users: [],
          select_all: false,
        };
      }
      sendToMap[teamId].users.push(userId);
    }
  });
  const sendToArray: SendToItem[] = Object.entries(sendToMap).map(
    ([teamId, data]) => {
      const isAllSelected: boolean = selectedValues.includes(
        `${teamId}::ALL_MEMBERS`
      );
      return {
        id: teamId,
        select_all: isAllSelected,
        users: isAllSelected ? [] : data.users,
        create_self: Array.isArray(teams)
          ? teams.find((t: Team) => t.id === teamId)?.permissions
              ?.create_self || false
          : false,
      };
    }
  );

  return { additionalRecipientValues, sendToArray };
}

/**
 * Parses and formats a shipment location address
 *
 * @param location - Location object containing address and parsed_address data
 * @returns Formatted address string
 */
export const getShipmentLocationParsedAddress = (
  location?: ShipmentLocation
): string => {
  let address = '';
  if (
    location?.parsed_address?.PlaceName ||
    location?.parsed_address?.StateName
  ) {
    address = [
      location?.parsed_address?.PlaceName,
      location?.parsed_address?.StateName,
      location?.parsed_address?.ZipCode,
      location?.parsed_address?.SubaddressIdentifier,
    ]
      .filter(Boolean)
      .join(', ');
  } else if (location?.parsed_address?.Recipient) {
    address = location?.parsed_address?.Recipient;
  } else if (typeof location?.address === 'string') {
    address = location.address;
  } else if (location?.address) {
    address = [
      location.address.line1,
      location.address.line2,
      location.address.city,
      location.address.stateProvince,
      location.address.postalCode,
      location.address.countryCode,
    ]
      .filter(Boolean)
      .join(', ');
  }
  return address;
};

export const formatDateTime = (dateString?: string): string => {
  if (!dateString) return '-';

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(dateString));
};

// this is a function which will convert enum to array, for labels to have space put '_' in enum
export const enumToArrayWithSpaces = <
  T extends Record<string, string | number>,
>(
  enumObj: T
): { label: string; value: T[keyof T] }[] => {
  return Object.keys(enumObj)
    .filter((key) => isNaN(Number(key)))
    .map((key) => {
      const stringKey = String(key);
      return {
        label: stringKey.includes('_')
          ? stringKey.replace(/_/g, ' ')
          : stringKey,
        value: enumObj[key as keyof T],
      };
    });
};
export const lowerLabel = (text: string) => text.toLowerCase();

const USER_DATE_FORMAT_TO_DAYJS: Record<string, string> = {
  'MM/DD/YYYY': 'MM-DD-YYYY',
  'DD/MM/YYYY': 'DD-MM-YYYY',
  'YYYY/MM/DD': 'YYYY-MM-DD',
};

/**
 * Parse a shipment datetime from the API. Detail / Control Tower payloads are
 * already in the user's profile date order with hyphens
 * (e.g. "08-15-2026 09:25:00"), not ISO. User preference is tried first so
 * ambiguous values like 05-08-2026 follow MM/DD vs DD/MM correctly.
 */
export const parseShipmentDayjs = (
  dateString?: string | null,
  userDateFormat?: string | null
): dayjs.Dayjs | null => {
  if (dateString == null) return null;

  const trimmed = String(dateString).trim();
  if (!trimmed) return null;

  const resolvedFormat = userDateFormat || getUserDateFormat();
  const userToken = USER_DATE_FORMAT_TO_DAYJS[resolvedFormat];

  const formats = [
    ...(userToken
      ? [`${userToken} HH:mm:ss`, `${userToken} HH:mm`, userToken]
      : []),
    'YYYY-MM-DD HH:mm:ss',
    'YYYY-MM-DDTHH:mm:ss',
    'YYYY-MM-DDTHH:mm',
    'YYYY-MM-DD',
    'MM-DD-YYYY HH:mm:ss',
    'DD-MM-YYYY HH:mm:ss',
    'MM-DD-YYYY HH:mm',
    'DD-MM-YYYY HH:mm',
    'MM-DD-YYYY',
    'DD-MM-YYYY',
    'YYYY-MM-DD HH:mm',
  ];

  const seen = new Set<string>();
  for (const format of formats) {
    if (seen.has(format)) continue;
    seen.add(format);
    const candidate = dayjs(trimmed, format, true);
    if (candidate.isValid()) return candidate;
  }

  if (
    /T|\d{4}-\d{2}-\d{2}/.test(trimmed) ||
    /[zZ]|[+-]\d{2}:\d{2}$/.test(trimmed)
  ) {
    const iso = dayjs(trimmed);
    if (iso.isValid()) return iso;
  }

  return null;
};

export const parseShipmentDateTime = (
  dateString?: string | null,
  userDateFormat?: string | null
): { date: dayjs.Dayjs; time: dayjs.Dayjs } | null => {
  const parsed = parseShipmentDayjs(dateString, userDateFormat);
  if (!parsed) return null;
  return { date: parsed, time: parsed };
};

// used in MarkAsDeliveredModal, EditShipmentDrawerWithTeams
export const combineDateAndTime = (
  date: dayjs.Dayjs | null,
  time: dayjs.Dayjs | null
): string => {
  if (!date?.isValid() || !time?.isValid()) return '';

  const combined = date
    .hour(time.hour())
    .minute(time.minute())
    .second(time.second());

  if (!combined.isValid()) return '';

  // Naive ISO local time; backend interprets this in the user's timezone.
  return combined.format('YYYY-MM-DDTHH:mm:ss');
};

//This a helper function which returns just user time zone, if user has set its timezone from profile then
// it retuns that else user browser time zone

interface DecodedToken {
  time_zone_enable?: boolean;
  user_time_zone?: string;
}

export const getUserTimeZoneValue = (decodedToken?: DecodedToken): string => {
  if (decodedToken?.time_zone_enable && decodedToken?.user_time_zone) {
    return decodedToken.user_time_zone;
  }

  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};
